import MyDialog from "~/mui/MyDialog";
import { useTrans } from '~/hooks/useTrans';
import { apiDeleteProjectCatalogGalleryItem, apiSortProjectCatalogGallery, apiUploadProjectCatalogGalleryItem } from '~/providers/api';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { IClientProjectCatalog, IClientProjectCatalogGallery } from "~/providers/client-interfaces";
import { IUrlParams } from "~/interfaces";
import WhatAbout from "~/components/WhatAbout";
import MyAlert from "~/mui/MyAlert";
import UploadPreviews from "~/components/UploadPreviews";
import { IPreviewItem, createEmptyItem } from "~/components/PreviewItem";
import { MAX_CATALOG_IMAGES, PREVIEW_MAX_FILE_SIZE_MB, PREVIEW_MAX_SIZE, PREVIEW_MIN_SIZE } from "~/const";

import "./style.less";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	previewsError: string;
	published: boolean;
	photoInfo: boolean;
	items: Array<IPreviewItem>;
}

interface IEditProjectCatalogGalleryModal {
	params: IUrlParams;
	gallery: Array<IClientProjectCatalogGallery>;
	isInternal: boolean;
	catalogData: IClientProjectCatalog;
	onClose?: () => void;
	onSave?: (gallery: Array<IClientProjectCatalogGallery>) => void;
}

function getPreviews(gallery: Array<IClientProjectCatalogGallery>) {
	const previews: Array<IPreviewItem> = gallery.map(item => ({
		id: item.id,
		imgSrc: item.images.small,
		state: "done",
		data: item,
	}));

	if (previews.length < MAX_CATALOG_IMAGES) {
		previews.push(createEmptyItem());
	}

	return previews;
}

export default function EditProjectCatalogGalleryModal({
	params,
	gallery,
	isInternal = false,
	catalogData = null,
	onClose = () => {},
	onSave = () => {},
}: IEditProjectCatalogGalleryModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		previewsError: "",
		published: catalogData.published,
		photoInfo: false,
		items: getPreviews(gallery),
	});

	async function onReorder(newItems: Array<IPreviewItem>) {
		// aktualizujeme a na pozadi provedem request na BE
		updateState({
			items: newItems,
		});

		const ids = newItems.filter(item => item.state !== "upload").map(item => item.id);
		const sortData = await apiSortProjectCatalogGallery(isInternal ? "admin" : "client", params.projectId, catalogData.id, ids, lang);

		if (!sortData.noErrors) {
			updateState({
				errorMsg: sortData.errors.join(", "),
			});
		}
	}

	function updateItems(items: Array<IPreviewItem>, id: IPreviewItem["id"], newData: Partial<IPreviewItem>): Array<IPreviewItem> {
		const copiedItems = items.slice();
		const filtered = copiedItems.filter(item => item.id === id);

		if (filtered.length === 1) {
			Object.assign(filtered[0], newData);
		}

		return copiedItems;
	}

	async function onUpload(item: IPreviewItem, file: File) {
		const items = updateItems(state.items, item.id, { state: "uploading" });

		updateState({
			items,
		});

		const uploadData = await apiUploadProjectCatalogGalleryItem(isInternal ? "admin" : "client", params.projectId, catalogData.id, file, lang);

		if (uploadData.noErrors) {
			const lastItem = uploadData.data[uploadData.data.length - 1];
			const newItems = updateItems(items, item.id, {
				id: lastItem.id,
				state: "done",
				imgSrc: lastItem.images.small,
				data: lastItem,
			});

			if (newItems.length < MAX_CATALOG_IMAGES) {
				newItems.push(createEmptyItem());
			}

			updateState({
				items: newItems,
				errorMsg: "",
			});
		} else {
			updateState({
				errorMsg: uploadData.errors.join(", "),
			});
		}
	}

	function onError(item: IPreviewItem, msg: string) {
		updateState({
			errorMsg: msg,
		});
	}

	async function onRemove(item: IPreviewItem) {
		const items = state.items.slice();
		const ind = items.findIndex(curItem => curItem.id === item.id);

		if (ind !== -1) {
			items.splice(ind, 1);
			items.filter(curItem => curItem.state === "upload").length === 0 && items.push(createEmptyItem());
			updateState({
				items,
			});
		}

		const deleteData = await apiDeleteProjectCatalogGalleryItem(isInternal ? "admin" : "client", params.projectId, catalogData.id, item.id, lang);

		if (!deleteData.noErrors) {
			updateState({
				errorMsg: deleteData.errors.join(", "),
			});
		}
	}

	function closeDialog() {
		const curGalleryItems = state.items.filter(item => item.data).map(item => item.data);

		onSave(curGalleryItems);
		onClose();
	}

	return <MyDialog title={trans("editProjectCatalogGalleryModal.mainTitle")} onClose={closeDialog} className="editProjectCatalogGalleryModalTopClass" contentClassName="editProjectCatalogGalleryModal"
		dialogLoading={state.dialogLoading} errorMsg={state.errorMsg} saveCaption={trans("saveChanges")} cancelCaption={trans("close")} contentSx={{ minWidth: "600px" }} onlyClose={true}>
		<div className="editProjectCatalogGalleryModal__content">
			<p className="editProjectCatalogGalleryModal__header">
				<span>
					{ trans("editProjectCatalogGalleryModal.header.title") }
				</span>
				<WhatAbout title={trans("editProjectCatalogGalleryModal.header.whatAbout")} onClick={() => updateState({ photoInfo: true })} />
			</p>
			<p>
				{ trans("editProjectCatalogGalleryModal.info", {
					count: MAX_CATALOG_IMAGES,
					rightSize: PREVIEW_MAX_FILE_SIZE_MB,
					minSize: PREVIEW_MIN_SIZE,
					maxSize: PREVIEW_MAX_SIZE,
				}) }
			</p>
			<UploadPreviews items={state.items} onReorder={onReorder} onUpload={onUpload} onError={onError} onRemove={onRemove} />
			<p>
				{ state.previewsError }
			</p>
		</div>
		{ state.photoInfo && <MyAlert title={trans("catalog.photoInfo.title")} onClose={() => updateState({ photoInfo: false })} cancelCaption={trans("close")}>
			<p>{ trans("catalog.photoInfo.desc") }</p>
		</MyAlert> }
	</MyDialog>;
}
